import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, UserCog, UserPlus, KeyRound, Trash2, Search, FileText, Sparkles, Building2, Mail, CheckSquare, Megaphone, Pin, Pencil, Plus, Eye, Users } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfirm } from "@/components/ConfirmDialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full access — settings, roles, everything", tone: "bg-red-50 text-[#DC2626] border-red-100" },
  { value: "hr", label: "HR", desc: "People ops, approvals, hiring", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  { value: "employee", label: "Employee", desc: "Self-service only", tone: "bg-slate-100 text-slate-700 border-slate-200" },
];

const roleTone = (r) => ROLES.find(x => x.value === r)?.tone || "bg-slate-100 text-slate-700";

export default function Settings() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-6" data-testid="settings-root">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin console
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage roles, user accounts, and system-wide configuration.</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users"><UserCog className="h-3.5 w-3.5 mr-1.5" /> Users & Roles</TabsTrigger>
          <TabsTrigger value="new-user" data-testid="tab-new-user"><UserPlus className="h-3.5 w-3.5 mr-1.5" /> Create Login</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="tab-announcements"><Megaphone className="h-3.5 w-3.5 mr-1.5" /> Announcements</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="h-3.5 w-3.5 mr-1.5" /> Document Assignment</TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> System</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersRoles currentUser={user} /></TabsContent>
        <TabsContent value="new-user"><CreateLogin /></TabsContent>
        <TabsContent value="announcements"><AnnouncementsManager currentUser={user} /></TabsContent>
        <TabsContent value="documents"><DocumentAssignment /></TabsContent>
        <TabsContent value="system"><SystemInfo /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Users & Roles ---------------- */
function UsersRoles({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [notifyReset, setNotifyReset] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRole, setBulkRole] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter(u => u.email.toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s));
  }, [users, q]);

  const selectableIds = filtered.filter(u => u.id !== currentUser.id).map(u => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    const ok = await confirm({
      tone: newRole === "admin" ? "warning" : "info",
      title: `Change role to ${newRole}?`,
      description: `${u.name} (${u.email}) will move from "${u.role}" to "${newRole}".`,
      confirmLabel: `Change to ${newRole}`,
    });
    if (!ok) return;
    try {
      await api.put(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.email} is now ${newRole}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyBulk = async () => {
    if (!bulkRole) return toast.error("Pick a role first");
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      tone: bulkRole === "admin" ? "warning" : "info",
      title: `Change role to ${bulkRole} for ${ids.length} users?`,
      description: `All selected accounts will move to the "${bulkRole}" role in one action.`,
      confirmLabel: `Apply to ${ids.length}`,
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-role", { user_ids: ids, role: bulkRole });
      toast.success(`Updated ${data.updated} account(s) to ${bulkRole}`);
      setBulkRole("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const deleteUser = async (u) => {
    const ok = await confirm({
      tone: "danger",
      title: `Delete login for ${u.email}?`,
      description: "This removes the sign-in account. The employee record stays intact — you can re-create the login any time.",
      confirmLabel: "Delete login",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReset = async () => {
    try {
      const { data } = await api.post(`/admin/users/${resetTarget.id}/reset-password`,
        { password: newPw, notify_email: notifyReset });
      if (data.email_sent) {
        toast.success(`Password reset for ${resetTarget.email} · emailed`);
      } else if (notifyReset && data.email_error) {
        toast.warning(`Password reset — but email failed: ${data.email_error}`);
      } else {
        toast.success(`Password reset for ${resetTarget.email}`);
      }
      setResetTarget(null); setNewPw(""); setNotifyReset(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-64 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="pl-9" data-testid="users-search" />
        </div>
        <Badge className="bg-slate-100 text-slate-700 border-0">{filtered.length} accounts</Badge>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 ml-auto bg-red-50/60 border border-red-200 rounded-lg px-3 py-1.5" data-testid="bulk-bar">
            <CheckSquare className="h-4 w-4 text-[#DC2626]" />
            <span className="text-sm font-medium text-[#DC2626]">{selectedIds.size} selected</span>
            <span className="text-slate-300">·</span>
            <span className="text-xs text-slate-500">Change role to</span>
            <Select value={bulkRole} onValueChange={setBulkRole}>
              <SelectTrigger className="w-32 h-8" data-testid="bulk-role-select"><SelectValue placeholder="Pick…" /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] h-8" disabled={!bulkRole || bulkBusy} onClick={applyBulk} data-testid="bulk-apply">
              Apply
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">Clear</Button>
          </div>
        )}
      </div>

      <Card className="rounded-xl border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" />
                </th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Employee ID</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(u => (
                <tr key={u.id} className={`hover:bg-slate-50/50 ${selectedIds.has(u.id) ? "bg-red-50/30" : ""}`} data-testid={`user-row-${u.email}`}>
                  <td className="px-3 py-3">
                    <Checkbox
                      checked={selectedIds.has(u.id)}
                      onCheckedChange={() => toggleOne(u.id)}
                      disabled={u.id === currentUser.id}
                      data-testid={`bulk-select-${u.email}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        {u.employee?.avatar_url && <AvatarImage src={`${API}${u.employee.avatar_url}`} alt={u.name} />}
                        <AvatarFallback className="bg-red-50 text-[#DC2626] text-xs font-semibold">
                          {(u.name || u.email).split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {u.employee ? <>
                      <div>{u.employee.designation}</div>
                      <div className="text-xs text-slate-500">{u.employee.department}</div>
                    </> : <span className="text-slate-400 text-xs italic">No employee record</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === currentUser.id}>
                      <SelectTrigger className="w-36 h-9" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">{u.employee_id?.slice(0, 8) || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)} data-testid={`reset-${u.email}`}>
                      <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset PW
                    </Button>
                    {u.id !== currentUser.id && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteUser(u)} data-testid={`del-${u.email}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-3">
        {ROLES.map(r => (
          <Card key={r.value} className={`p-4 rounded-xl border ${r.tone}`}>
            <div className="font-semibold text-sm">{r.label}</div>
            <div className="text-xs opacity-80 mt-1">{r.desc}</div>
          </Card>
        ))}
      </div>

      <Dialog open={!!resetTarget} onOpenChange={() => { setResetTarget(null); setNewPw(""); setNotifyReset(true); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {resetTarget?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New password (min 8 chars)</Label>
              <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Techtreno@2026" data-testid="reset-pw-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={notifyReset} onCheckedChange={(v) => setNotifyReset(!!v)} data-testid="reset-notify-checkbox" />
              <Mail className="h-4 w-4 text-slate-500" />
              Email the new password to {resetTarget?.email}
            </label>
            <div className="text-[11px] text-slate-500">Sent via Microsoft 365. If email fails, the password change still applies — share manually.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPw(""); }}>Cancel</Button>
            <Button onClick={submitReset} disabled={newPw.length < 8} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="reset-pw-submit">Set password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Create login for existing employee ---------------- */
function CreateLogin() {
  const [emps, setEmps] = useState([]);
  const [pick, setPick] = useState("");
  const [role, setRole] = useState("employee");
  const [password, setPassword] = useState("Techtreno@2026");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [result, setResult] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/employees-without-users"); setEmps(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!pick) return toast.error("Select an employee");
    try {
      const { data } = await api.post("/admin/users/create-for-employee",
        { employee_id: pick, role, password, notify_email: notifyEmail });
      if (data.email_sent) toast.success(`Login created & credentials emailed to ${data.email}`);
      else if (notifyEmail && data.email_error) toast.warning(`Login created — but email failed: ${data.email_error}`);
      else toast.success(`Login created for ${data.email}`);
      setResult(data);
      setPick(""); setPassword("Techtreno@2026"); setRole("employee");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="p-5 rounded-xl border-slate-200/60 space-y-4">
        <div>
          <div className="font-semibold text-slate-900">Grant HRMS access to an employee</div>
          <div className="text-xs text-slate-500">Employees below have profiles but no login. Create one so they can sign in.</div>
        </div>

        {emps.length === 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg">All employees already have login accounts.</div>
        ) : (
          <>
            <div>
              <Label>Employee ({emps.length} without login)</Label>
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger data-testid="new-user-emp"><SelectValue placeholder="Choose an employee…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {emps.map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation} ({e.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Initial password</Label>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-pw" />
              </div>
            </div>
            <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer rounded-lg border border-slate-200 p-3 hover:border-red-200 hover:bg-red-50/30 transition-colors">
              <Checkbox checked={notifyEmail} onCheckedChange={(v) => setNotifyEmail(!!v)} className="mt-0.5" data-testid="notify-checkbox" />
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2"><Mail className="h-4 w-4 text-[#DC2626]" /> Email sign-in details to the employee</div>
                <div className="text-xs text-slate-500 mt-0.5">Sent via Microsoft 365 from your Techtreno mailer.</div>
              </div>
            </label>
            <Button onClick={create} disabled={!pick || password.length < 8} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="new-user-submit">
              <UserPlus className="h-4 w-4 mr-2" /> Create login
            </Button>
          </>
        )}

        {result && (
          <div className={`mt-3 rounded-lg border p-3 text-sm ${result.email_sent ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}>
            <div className={`font-semibold ${result.email_sent ? "text-emerald-800" : "text-amber-800"}`}>
              Login created {result.email_sent ? "· email sent ✓" : "✓"}
            </div>
            <div className={result.email_sent ? "text-emerald-700 mt-1" : "text-amber-700 mt-1"}>Email: <span className="font-mono">{result.email}</span></div>
            {!result.email_sent && <div className={result.email_sent ? "text-emerald-700" : "text-amber-700"}>Password: <span className="font-mono">{result.initial_password}</span></div>}
            {result.email_sent
              ? <div className="text-xs text-emerald-600/80 mt-2">Credentials delivered to their inbox.</div>
              : <div className="text-xs text-amber-600/80 mt-2">
                  {result.email_error ? `Email failed: ${result.email_error}` : "Share credentials securely; the user should change their password on first login."}
                </div>}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Document Assignment ---------------- */
function DocumentAssignment() {
  const [employees, setEmployees] = useState([]);
  const [pick, setPick] = useState("");
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("Personal");
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const { data } = await api.get("/employees");
    setEmployees(data);
  };
  useEffect(() => { load(); }, []);

  const loadDocs = async (empId) => {
    if (!empId) { setDocs([]); return; }
    try { const { data } = await api.get(`/documents?employee_id=${empId}`); setDocs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadDocs(pick); }, [pick]);

  const upload = async () => {
    if (!pick) return toast.error("Select an employee first");
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("employee_id", pick);
      if (description) fd.append("description", description);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document assigned");
      setFile(null); setDescription("");
      loadDocs(pick);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeDoc = async (id) => {
    const ok = await confirm({ tone: "danger", title: "Delete this document?", description: "This cannot be undone.", confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); loadDocs(pick); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 rounded-xl border-slate-200/60 space-y-4">
        <div>
          <div className="font-semibold text-slate-900">Assign documents to employees</div>
          <div className="text-xs text-slate-500">Offer letters, ID proofs, appraisal letters — anything an employee should have in their vault.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Employee</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="assign-doc-emp"><SelectValue placeholder="Choose an employee…" /></SelectTrigger>
              <SelectContent className="max-h-72">{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Offer Letter", "Appointment Letter", "Appraisal Letter", "ID Proof", "Address Proof", "Bank Detail", "Personal", "Other"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>File</Label>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="assign-doc-file" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Signed offer for FY25" />
          </div>
        </div>
        <Button onClick={upload} disabled={busy || !pick || !file} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="assign-doc-submit">
          <FileText className="h-4 w-4 mr-2" /> Upload & assign
        </Button>
      </Card>

      {pick && (
        <Card className="p-4 rounded-xl border-slate-200/60">
          <div className="font-semibold text-slate-900 text-sm mb-3">Documents on file ({docs.length})</div>
          {docs.length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">No documents assigned yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {docs.map(d => (
                <div key={d.id} className="flex items-center gap-3 py-2 text-sm">
                  <FileText className="h-4 w-4 text-[#DC2626]" />
                  <div className="flex-1">
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.category} · uploaded by {d.uploaded_by}</div>
                  </div>
                  <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[#DC2626] hover:underline">Download</a>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeDoc(d.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ---------------- System info ---------------- */
function SystemInfo() {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 rounded-xl border-slate-200/60">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 grid place-items-center"><Building2 className="h-5 w-5 text-[#DC2626]" /></div>
            <div className="font-semibold text-slate-900">Techtreno Solutions Pvt Ltd</div>
          </div>
          <div className="space-y-2 text-sm text-slate-600">
            <Row k="Environment" v="Production" />
            <Row k="Backend" v="FastAPI + MongoDB" />
            <Row k="Auth" v="JWT · Microsoft SSO · 2FA (TOTP + Email)" />
            <Row k="Email" v="Microsoft 365 Graph" />
          </div>
        </Card>
        <Card className="p-5 rounded-xl border-slate-200/60">
          <div className="font-semibold text-slate-900 mb-3">What admins can do</div>
          <ul className="text-sm text-slate-600 space-y-1.5 list-disc list-inside">
            <li>Change any user's role (admin, HR, employee)</li>
            <li>Create logins for existing employees</li>
            <li>Reset any user's password</li>
            <li>Assign documents to any employee</li>
            <li>Upload employee photos from anywhere</li>
            <li>Swap the company login wallpaper</li>
            <li>Bulk-archive or reorg employees</li>
            <li>Full access to every module</li>
          </ul>
        </Card>
      </div>
      <LoginWallpaper />
    </div>
  );
}

function LoginWallpaper() {
  const [meta, setMeta] = useState({ set: false });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const load = async () => {
    try { const { data } = await api.get("/settings/login-wallpaper/meta"); setMeta(data); }
    catch { setMeta({ set: false }); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files (JPG, PNG, WebP)");
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8 MB");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/settings/login-wallpaper", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Login wallpaper updated — visible on the next sign-in screen");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const reset = async () => {
    const ok = await confirm({ tone: "warning", title: "Reset to default wallpaper?", description: "Your custom image will be removed. The default data-centre photo will show on the login screen.", confirmLabel: "Reset wallpaper" });
    if (!ok) return;
    try { await api.delete("/settings/login-wallpaper"); toast.success("Reset to default"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const preview = meta.set ? `${API}/settings/login-wallpaper?v=${meta.version}` :
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=1600&q=80";

  return (
    <Card className="p-5 rounded-xl border-slate-200/60" data-testid="login-wallpaper-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#DC2626]" /> Company login wallpaper</div>
          <div className="text-xs text-slate-500 mt-0.5">Swap the sign-in page image for seasonal or event branding. Recommended: 1600×900 landscape, ≤ 8 MB.</div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="wallpaper-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="wallpaper-upload-btn">
            <FileText className="h-4 w-4 mr-1.5" /> {meta.set ? "Change image" : "Upload image"}
          </Button>
          {meta.set && (
            <Button variant="outline" className="text-red-600 border-red-200" onClick={reset} disabled={busy} data-testid="wallpaper-reset-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> Reset
            </Button>
          )}
        </div>
      </div>
      <div className="relative aspect-[16/9] rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
        <img src={preview} alt="Login preview" className="absolute inset-0 h-full w-full object-cover" data-testid="wallpaper-preview" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/85 via-[#0F172A]/40 to-[#DC2626]/40" />
        <div className="absolute bottom-4 left-4 text-white">
          <div className="text-[10px] uppercase tracking-widest text-red-200 font-semibold">Preview</div>
          <div className="font-display font-bold text-xl mt-1">Complete data-centre solutions</div>
        </div>
      </div>
      {meta.set && (
        <div className="text-[11px] text-slate-500 mt-3">
          Updated by <span className="font-medium">{meta.updated_by}</span> · {meta.updated_at ? new Date(meta.updated_at).toLocaleString("en-IN") : ""}
        </div>
      )}
    </Card>
  );
}
function Row({ k, v }) { return <div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>; }

/* ---------------- Announcements Manager ---------------- */
const SEVERITIES = [
  { value: "info", label: "Info", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  { value: "warning", label: "Warning", tone: "bg-amber-50 text-amber-700 border-amber-100" },
  { value: "urgent", label: "Urgent", tone: "bg-red-50 text-[#DC2626] border-red-100" },
];
const sevTone = (v) => SEVERITIES.find(s => s.value === v)?.tone || "bg-slate-100 text-slate-700 border-slate-200";

function AnnouncementsManager({ currentUser }) {
  const [rows, setRows] = useState([]);
  const [readCounts, setReadCounts] = useState({});
  const [editing, setEditing] = useState(null); // full row or "new"
  const [reads, setReads] = useState(null); // reads viewer state
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/announcements"),
        api.get("/announcements/read-counts"),
      ]);
      setRows(r1.data);
      setReadCounts(r2.data || {});
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openReads = async (row) => {
    try {
      const { data } = await api.get(`/announcements/${row.id}/reads`);
      setReads(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (row) => {
    const ok = await confirm({
      tone: "danger",
      title: `Delete "${row.title}"?`,
      description: "This announcement will disappear from every dashboard immediately.",
      confirmLabel: "Delete announcement",
    });
    if (!ok) return;
    try { await api.delete(`/announcements/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePinned = async (row) => {
    try { await api.put(`/announcements/${row.id}`, { pinned: !row.pinned }); load(); toast.success(row.pinned ? "Unpinned" : "Pinned to top"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (row) => {
    try { await api.put(`/announcements/${row.id}`, { active: !(row.active !== false) }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="announcements-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-slate-900">Company announcements</div>
          <div className="text-xs text-slate-500">Pinned announcements appear as a banner across the top of everyone's dashboard.</div>
        </div>
        <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => setEditing({ title: "", body: "", severity: "info", pinned: false, active: true, expires_at: "" })} data-testid="new-announcement-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New announcement
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500 rounded-xl border-dashed border-slate-200">No announcements yet. Post one so the whole company sees it.</Card>
      ) : (
        <div className="space-y-3">
          {rows.map(row => (
            <Card key={row.id} className={`p-4 rounded-xl border-slate-200/60 ${row.pinned ? "ring-1 ring-[#DC2626]/30 bg-red-50/20" : ""}`} data-testid={`ann-row-${row.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900">{row.title}</div>
                    <Badge className={`${sevTone(row.severity)} border`}>{row.severity || "info"}</Badge>
                    {row.pinned && <Badge className="bg-red-50 text-[#DC2626] border-red-100 border"><Pin className="h-3 w-3 mr-1" /> Pinned</Badge>}
                    {row.active === false && <Badge className="bg-slate-100 text-slate-500 border-0">Hidden</Badge>}
                    {((row.audience_roles?.length || 0) + (row.audience_departments?.length || 0)) > 0 && (
                      <Badge className="bg-slate-100 text-slate-700 border-0 hover:bg-slate-100">
                        Targeted · {[...(row.audience_roles || []).map(r => r), ...(row.audience_departments || [])].slice(0, 3).join(", ")}
                        {((row.audience_roles?.length || 0) + (row.audience_departments?.length || 0)) > 3 && "…"}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{row.body}</div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    by <span className="font-medium">{row.author}</span> · {new Date(row.created_at).toLocaleString("en-IN")}
                    {row.expires_at && <> · expires {new Date(row.expires_at).toLocaleDateString("en-IN")}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => openReads(row)} title="Who's seen this?" data-testid={`reads-${row.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    <span className="text-xs font-mono">{readCounts[row.id] || 0}</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePinned(row)} title={row.pinned ? "Unpin" : "Pin to top"}>
                    <Pin className={`h-3.5 w-3.5 ${row.pinned ? "fill-[#DC2626] text-[#DC2626]" : ""}`} />
                  </Button>
                  <div className="flex items-center gap-2 pl-2 pr-1 border-l">
                    <span className="text-[11px] text-slate-500">Visible</span>
                    <Switch checked={row.active !== false} onCheckedChange={() => toggleActive(row)} />
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(row)} data-testid={`edit-ann-${row.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(row)} data-testid={`del-ann-${row.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AnnouncementDialog value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ReadsDialog data={reads} onClose={() => setReads(null)} />
    </div>
  );
}

function ReadsDialog({ data, onClose }) {
  if (!data) return null;
  const pct = data.total_audience > 0 ? Math.round((data.read_count / data.total_audience) * 100) : 0;
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> Read receipts</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600 -mt-2 mb-3">"{data.title}"</div>

        {/* Big number */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 mb-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Seen by</div>
              <div className="font-display text-4xl font-bold text-slate-900">
                {data.read_count}
                <span className="text-slate-400 text-2xl"> / {data.total_audience}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Reach</div>
              <div className={`font-display text-3xl font-bold ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-[#DC2626]"}`}>{pct}%</div>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full ${pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-[#DC2626]"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 mb-1.5">Seen ({data.reads.length})</div>
            <div className="rounded-lg border border-slate-200 max-h-64 overflow-y-auto p-2 space-y-1">
              {data.reads.length === 0 ? <div className="text-xs text-slate-400 py-3 text-center">No one yet</div> :
                data.reads.map(r => (
                  <div key={r.user_id} className="text-xs px-2 py-1 rounded bg-emerald-50/50">
                    <div className="font-medium text-slate-800">{r.user_name}</div>
                    <div className="text-[10px] text-slate-500">{new Date(r.read_at).toLocaleString("en-IN")}</div>
                  </div>
                ))
              }
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#DC2626] mb-1.5">Not seen yet ({data.unread_count})</div>
            <div className="rounded-lg border border-slate-200 max-h-64 overflow-y-auto p-2 space-y-1">
              {data.unread.length === 0 ? <div className="text-xs text-emerald-600 py-3 text-center">Everyone's caught up!</div> :
                data.unread.map(u => (
                  <div key={u.user_id} className="text-xs px-2 py-1 rounded bg-red-50/40">
                    <div className="font-medium text-slate-800">{u.name}</div>
                    <div className="text-[10px] text-slate-500">{u.email}</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [departments, setDepartments] = useState([]);
  useEffect(() => { setF(value ? { ...value } : null); }, [value]);
  useEffect(() => {
    if (value) api.get("/departments").then(r => setDepartments(r.data)).catch(() => {});
  }, [value]);
  if (!f) return null;
  const isNew = !f.id;
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const roles = f.audience_roles || [];
  const depts = f.audience_departments || [];
  const targeted = roles.length + depts.length > 0;

  const toggleRole = (r) => set("audience_roles", roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r]);
  const toggleDept = (d) => set("audience_departments", depts.includes(d) ? depts.filter(x => x !== d) : [...depts, d]);

  const save = async () => {
    if (!f.title?.trim() || !f.body?.trim()) return toast.error("Title and body are required");
    setBusy(true);
    try {
      const payload = {
        title: f.title.trim(), body: f.body.trim(),
        severity: f.severity || "info",
        pinned: !!f.pinned, active: f.active !== false,
        expires_at: f.expires_at || null,
        audience_roles: roles,
        audience_departments: depts,
      };
      if (isNew) await api.post("/announcements", payload);
      else await api.put(`/announcements/${f.id}`, payload);
      toast.success(isNew ? "Announcement posted" : "Updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="ann-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New announcement" : "Edit announcement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Holi Holiday · March 6" data-testid="ann-title" /></div>
          <div><Label>Body *</Label><Textarea rows={4} value={f.body} onChange={(e) => set("body", e.target.value)} placeholder="Offices will be closed for Holi. Wishing you all a colorful and safe celebration!" data-testid="ann-body" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Severity</Label>
              <Select value={f.severity || "info"} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger data-testid="ann-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expires on (optional)</Label>
              <Input type="date" value={(f.expires_at || "").slice(0, 10)} onChange={(e) => set("expires_at", e.target.value ? new Date(e.target.value).toISOString() : null)} />
            </div>
          </div>
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox checked={!!f.pinned} onCheckedChange={(v) => set("pinned", !!v)} data-testid="ann-pinned" />
              <Pin className="h-3.5 w-3.5 text-[#DC2626]" /> Pin to dashboard banner
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Switch checked={f.active !== false} onCheckedChange={(v) => set("active", !!v)} />
              Visible to everyone
            </label>
          </div>

          {/* --- Audience Reach --- */}
          <div className="pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">Audience reach</div>
                <div className="text-[11px] text-slate-500">Empty = show to everyone. Pick roles and/or departments to target specific groups.</div>
              </div>
              <Badge className={targeted ? "bg-red-50 text-[#DC2626] border-red-100 border" : "bg-slate-100 text-slate-600 border-0"}>
                {targeted ? "Targeted" : "Company-wide"}
              </Badge>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Roles</Label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {ROLES.map(r => {
                  const on = roles.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => toggleRole(r.value)}
                      className={`px-3 h-8 rounded-full text-xs font-medium border transition-all ${on ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                      data-testid={`aud-role-${r.value}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Departments</Label>
              <div className="flex gap-2 mt-1 flex-wrap max-h-32 overflow-y-auto">
                {departments.length === 0 && <span className="text-xs text-slate-400 italic">Loading…</span>}
                {departments.map(d => {
                  const on = depts.includes(d.name);
                  return (
                    <button
                      key={d.name}
                      type="button"
                      onClick={() => toggleDept(d.name)}
                      className={`px-3 h-8 rounded-full text-xs font-medium border transition-all ${on ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                      data-testid={`aud-dept-${d.name}`}
                    >
                      {d.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="ann-save">
            {busy ? "Saving…" : isNew ? "Post announcement" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

